<template>
  <Drawer
    :id="drawerId"
    :title="title"
    :loading="editLoading || loadingInitialData"
    :iframed="iframed"
    preloadContent
  >
    <!-- Custom Back Button (for navigating between print cart screens) -->
    <template
      v-if="showBack"
      #customBack
    >
      <mcs-btn
        icon
        color="black"
        prepend-icon="mdi-keyboard-backspace"
        @click="back"
      ></mcs-btn>
    </template>

    <!-- Shopping cart icon in the header of the drawer -->
    <template
      v-if="total_lines && !iframed"
      #cart
    >
      <CartButton
        disable-load-data
        @click.native.stop="showEdit = false"
      />
    </template>

    <!-- Show the alert promo bar on iframed checkouts -->
    <div
      v-if="iframed"
      class="relative"
    >
      <AlertBar />
    </div>

    <!-- The Stepper. Details -> Cart -> Checkout. We show this whenever they aren't selecting from their list of projects -->
    <div class="px-6 py-4">
      <v-stepper
        v-model="step"
        alt-labels
        flat
        tile
      >
        <v-stepper-header>
          <v-stepper-step
            step="1"
            :complete="step > 1"
            :color="step > 3 ? 'success' : 'black'"
            class="pr-2"
          >
            Details
          </v-stepper-step>
          <v-divider />
          <v-stepper-step
            step="2"
            :complete="step > 2"
            :color="step > 3 ? 'success' : 'black'"
            class="px-5"
            :class="{ 'hover-pointer': enableCartStepperClick }"
            v-on="
              enableCartStepperClick
                ? {
                    click: () => {
                      setActiveScreen(cartScreen);
                    }
                  }
                : {}
            "
          >
            Cart
          </v-stepper-step>
          <v-divider />
          <v-stepper-step
            step="3"
            :complete="step > 3"
            :color="step > 3 ? 'success' : 'black'"
          >
            Checkout
          </v-stepper-step>
        </v-stepper-header>
      </v-stepper>
    </div>

    <!-- Show the project thumbnail + proof-->
    <ProjectDetails
      v-if="(showEditScreen || editLoading) && project.id"
      :iframed="iframed"
      @remove="setActiveScreen(cartScreen)"
      @refreshPrice="$refs.edit.getPrice()"
    />
    <PrintEditItem
      v-show="showEditScreen"
      ref="edit"
      :drawerId="drawerId"
      :hideFooter="!showEditScreen"
      :iframed="iframed"
      :calculateSalesTax="showCheckoutScreen"
      @save="setActiveScreen(cartScreen)"
      @selectProject="setActiveScreen(editScreen)"
      @loaded="editorLoaded"
    />
    <PromoEditItem
      v-show="showPromoEditScreen"
      :show="showPromoEditScreen"
      ref="editPromo"
      :hideFooter="!showPromoEditScreen"
      :drawerId="drawerId"
      @loaded="editorLoaded"
      @save="setActiveScreen(cartScreen)"
    />
    <PrintCart
      v-if="showCartScreen"
      :drawerId="drawerId"
      :iframed="iframed"
      @edit="editItem"
      @edit-shipping="editItem($event, true)"
      @add="addAnother"
      @checkout="setActiveScreen(checkoutScreen)"
      @empty="onCartEmpty"
    />
    <PrintCheckout
      v-else-if="showCheckoutScreen"
      :drawerId="drawerId"
      @back="showCheckout = false"
      @confirm="onConfirm"
      @cart="setActiveScreen(cartScreen)"
    />
    <PrintConfirmation
      v-else-if="showConfirmationScreen"
      :order="order"
      :iframed="iframed"
      :drawerId="drawerId"
    />
  </Drawer>
</template>

<script>
import PrintCart from '@/components/print/checkout/Cart';
import PrintCheckout from '@/components/print/checkout/Checkout';
import PrintConfirmation from '@/components/print/checkout/Confirmation';
import PrintEditItem from '@/components/print/checkout/edititem/Index';
import PromoEditItem from '@/components/print/checkout/edititem/promo/Index';
import {
  ANALYTICS_EVENTS,
  APP_EVENTS,
  DRAWERS,
  ENUMS,
  PRINT_CHECKOUT_SCREENS
} from '@/utils/constants';
import { mapGetters } from 'vuex';
import CartButton from '../../../components/header/CartButton';
import Drawer from '../../../components/layout/Drawer';
import ProjectDetails from '../../../components/print/checkout/ProjectDetails';
import AlertBar from '../../layout/AlertBar';

export default {
  name: 'PrintCheckoutDrawer',

  components: {
    Drawer,
    CartButton,
    ProjectDetails,
    PrintEditItem,
    PrintConfirmation,
    PrintCheckout,
    PrintCart,
    AlertBar,
    PromoEditItem
  },

  props: {
    iframed: Boolean
  },

  data() {
    return {
      drawerId: DRAWERS.PRINT_CHECKOUT,
      editScreen: PRINT_CHECKOUT_SCREENS.EDIT,
      activeScreen: PRINT_CHECKOUT_SCREENS.EDIT,
      cartScreen: PRINT_CHECKOUT_SCREENS.CART,
      checkoutScreen: PRINT_CHECKOUT_SCREENS.CHECKOUT,
      confirmationScreen: PRINT_CHECKOUT_SCREENS.CONFIRMATION,
      saving: false,
      valid: true,
      loadingInitialData: false,
      editLoading: false,
      order: {}
    };
  },

  computed: {
    step() {
      if (this.showEditScreen || this.showPromoEditScreen) {
        return 1;
      } else if (this.showCartScreen) {
        return 2;
      } else if (this.showCheckoutScreen) {
        return 3;
      } else if (this.showConfirmationScreen) {
        return 4;
      }
      return 1;
    },

    title() {
      if (this.showEditScreen) {
        if (this.edit_item.categoryId) {
          return `${
            this.categories.filter((x) => x.id === this.edit_item.categoryId).map((x) => x.name)[0]
          } Prints`;
        }
        return 'Select Print Options';
      } else if (this.showPromoEditScreen) {
        return 'Promotional Product';
      } else if (this.showCheckoutScreen) {
        return 'Payment';
      } else if (this.showConfirmationScreen) {
        return 'Thanks for your order!';
      } else {
        var s = this.total_lines > 1 ? 's' : '';
        return `Your Cart (${this.total_lines} item${s})`;
      }
    },

    showBack() {
      if (this.iframed) {
        if (this.showCheckoutScreen) return true;
        return false;
      }
      if (this.showCheckoutScreen) return true;
      if ((this.showEditScreen || this.showPromoEditScreen) && this.total_lines) return true;
      return false;
    },

    showCartScreen() {
      return this.activeScreen === PRINT_CHECKOUT_SCREENS.CART;
    },

    showEditScreen() {
      return this.activeScreen === PRINT_CHECKOUT_SCREENS.EDIT;
    },

    showPromoEditScreen() {
      return this.activeScreen === PRINT_CHECKOUT_SCREENS.EDIT_PROMO;
    },

    showCheckoutScreen() {
      return this.activeScreen === PRINT_CHECKOUT_SCREENS.CHECKOUT;
    },

    showConfirmationScreen() {
      return this.activeScreen === PRINT_CHECKOUT_SCREENS.CONFIRMATION;
    },

    enableCartStepperClick() {
      if (!this.total_lines) return false;
      return !(this.showConfirmationScreen || this.showCartScreen);
    },

    ...mapGetters({
      products: 'printcart/products',
      delivery: 'printcart/delivery',
      addresses: 'user/addresses',
      cart: 'printcart/cart',
      cart_lines: 'printcart/cart_lines',
      total_lines: 'printcart/total_lines',
      edit_item: 'printcart/edit_item',
      initialData: 'drawers/data',
      drawerOpenedAt: 'drawers/lastOpenedAt',
      categories: 'templates/printableCategories',
      project: 'printcart/project',
      getProject: 'projects/project',
      authorized: 'user/authorized',
      core_products: 'templates/core_products'
    })
  },

  watch: {
    drawerOpenedAt() {
      if (this.loadingInitialData) return;
      if (this.drawerId === this.$store.getters['drawers/topDrawer']?.id) {
        this.loadData();
        this.$store.dispatch('printcart/trackView', this.initialData(this.drawerId).projectId);
      }
    },

    activeScreen() {
      var contentEl = document
        .getElementById(this.drawerId)
        .closest('.v-navigation-drawer')
        .querySelector('.v-navigation-drawer__content');
      if (contentEl) contentEl.scrollTo({ top: 0, behavior: 'smooth' });
    }
  },

  mounted() {
    this.$eventBus.$on(APP_EVENTS.REFRESH_PRINT_DRAWER, () => {
      this.selectInitialScreen();
    });
  },

  methods: {
    selectInitialScreen() {
      const initialData = this.initialData(this.drawerId) || {};
      console.log('initialData', initialData);
      var screen = initialData.screen;
      if (initialData.projectId) {
        if (
          this.$store.getters['printcart/isProjectInCart'](initialData.projectId) &&
          this.getProject(initialData.projectId).printApproved
        ) {
          screen = PRINT_CHECKOUT_SCREENS.CART;
        } else {
          screen = PRINT_CHECKOUT_SCREENS.EDIT;
        }
      }

      if (screen === PRINT_CHECKOUT_SCREENS.EDIT && initialData.projectId) {
        this.selectProject(this.getProject(initialData.projectId));
        return;
      } else if (screen === PRINT_CHECKOUT_SCREENS.EDIT_PROMO) {
        // do nothing
      } else if (!this.total_lines && !this.iframed) {
        let coreProductId = initialData.coreProductId;
        const categoryId = initialData.categoryId;
        // make sure that the core product being set is mailable if using a campaign tpye id
        if (initialData.campaignTypeId && !coreProductId) {
          coreProductId = this.core_products(categoryId)
            .filter((x) => x.mailable)
            .map((x) => x.id)[0];
        }
        this.editItem({
          categoryId: categoryId,
          coreProductId: coreProductId
        });
        // show snailblast drawer on load
        if (initialData.campaignTypeId) {
          this.$store
            .dispatch('printcart/setCampaign', { typeId: initialData.campaignTypeId })
            .then(() => {
              this.$store.dispatch('drawers/open', {
                drawerId: DRAWERS.SNAILBLAST_AUDIENCE,
                data: {
                  expanded: initialData.campaignTypeId !== ENUMS.SNAILBLAST.CAMPAIGN_TYPE.UPLOAD
                }
              });
            });
        }
        return;
      }

      if (initialData) {
        if (initialData.categoryId) {
          this.editItem({
            categoryId: initialData.categoryId,
            coreProductId: initialData.coreProductId
          });
        }
        if (initialData.productId) {
          this.editItem(initialData);
        }
        this.$store.commit('drawers/CLEAR_DATA', this.drawerId);
      }
      this.setActiveScreen(screen);
    },

    editorLoaded() {
      this.showEdit = true;
      console.log('edit loading is false');
      this.editLoading = false;
    },

    setActiveScreen(val) {
      if (val) {
        this.activeScreen = val;
      }
    },

    back() {
      if (this.showCheckoutScreen || (this.showEdit && this.total_lines)) {
        this.setActiveScreen(PRINT_CHECKOUT_SCREENS.CART);
      }
    },

    loadData(project) {
      this.loadingInitialData = true;
      this.order = {};
      let calls = [this.$store.dispatch('printcart/setEditItem', {})];
      if (this.authorized) {
        calls = calls.concat([
          this.$store.dispatch('printcart/getCart'),
          this.$store.dispatch('projects/getDesigns'),
          this.$store.dispatch('projects/getUploads')
        ]);
      }
      Promise.all(calls)
        .then(() => {
          if (project) {
            this.selectProject(project);
          }
        })
        .catch((error) => {
          this.logError(error);
          console.log(project);
          this.$mixpanel.trackEvent(
            ANALYTICS_EVENTS.PRINT.NAME,
            ANALYTICS_EVENTS.PRINT.ACTIONS.CART_ERROR,
            { message: 'Error loading checkout. ' + error.message }
          );
          this.$root.$alert('Error Occurred', 'An error occurred while loading print checkout.');
        })
        .finally(() => {
          this.selectInitialScreen();
          this.loadingInitialData = false;
        });
    },

    addAnother() {
      let categoryId = null;
      if (this.cart_lines.length) {
        categoryId = this.cart_lines.map((x) => x.categoryId)[this.cart_lines.length - 1];
      }
      this.editItem({ categoryId });
    },

    selectProject(project) {
      this.editItem({ project });
    },

    editItem(item, showShipping) {
      const isPromo = !!item.productId;
      this.setActiveScreen(
        isPromo ? PRINT_CHECKOUT_SCREENS.EDIT_PROMO : PRINT_CHECKOUT_SCREENS.EDIT
      );
      this.$store.dispatch('printcart/setEditItem', item).then(() => {
        this.editLoading = true;
        (isPromo ? this.$refs.editPromo : this.$refs.edit).loadData(showShipping);
      });
    },

    onConfirm(order) {
      this.$store.commit('user/UPDATE_HAS_ORDERED', true);
      this.order = order;
      this.setActiveScreen(this.confirmationScreen);
    },

    onCartEmpty() {
      this.selectInitialScreen();
    }
  }
};
</script>

<style lang="scss" scoped>
.preview-background {
  background: transparent linear-gradient(180deg, #ffffff 0%, #ececefd9 100%) 0% 0% no-repeat
    padding-box;
}
::v-deep .v-stepper__step {
  flex-basis: auto !important;
  padding: 0px;
  .v-stepper__label {
    font-size: 10px;
    font-weight: 600;
  }

  &.v-stepper__step--inactive {
    .v-stepper__step__step {
      background-color: white !important;
      color: black !important;
      border: solid 1px rgba(0, 0, 0, 0.38) !important;
    }
    .v-stepper__label {
      color: black !important;
    }
  }
}

::v-deep .v-stepper__header .v-divider {
  margin: 10px 0 0 0 !important;
  border-top: dotted 3px black !important;
}
>>> .page-preview {
  border: solid 1.5px;
  border-radius: 5px;
  max-width: 100px;
  max-height: 35px;
}
</style>
